'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, Cell } from 'recharts';

const ACCURACY_TIMELINE = [
  { date: 'Oct', rf: 71.2, xgb: 69.8, lgbm: 68.9, elo: 67.1, lr: 65.8 },
  { date: 'Nov', rf: 72.8, xgb: 70.9, lgbm: 69.7, elo: 67.8, lr: 66.2 },
  { date: 'Dec', rf: 73.1, xgb: 71.4, lgbm: 70.2, elo: 68.2, lr: 66.8 },
  { date: 'Jan', rf: 73.9, xgb: 71.8, lgbm: 70.8, elo: 68.9, lr: 67.1 },
  { date: 'Feb', rf: 74.1, xgb: 72.1, lgbm: 71.1, elo: 69.3, lr: 67.4 },
  { date: 'Mar', rf: 74.3, xgb: 72.3, lgbm: 71.4, elo: 69.5, lr: 67.8 },
  { date: 'Apr', rf: 74.3, xgb: 72.3, lgbm: 71.4, elo: 69.5, lr: 67.8 },
];

const TEAM_ACCURACY = [
  { team: 'COL', accuracy: 81.2, games: 82 },
  { team: 'CAR', accuracy: 79.8, games: 82 },
  { team: 'TOR', accuracy: 78.4, games: 82 },
  { team: 'EDM', accuracy: 77.1, games: 82 },
  { team: 'NYR', accuracy: 76.8, games: 82 },
  { team: 'BOS', accuracy: 75.3, games: 82 },
  { team: 'FLA', accuracy: 73.9, games: 82 },
  { team: 'VGK', accuracy: 72.4, games: 82 },
  { team: 'DAL', accuracy: 71.8, games: 82 },
  { team: 'WSH', accuracy: 70.2, games: 82 },
];

const HOME_AWAY_DATA = [
  { model: 'Random Forest', home: 78.1, away: 70.5 },
  { model: 'XGBoost', home: 76.3, away: 68.2 },
  { model: 'LightGBM', home: 75.2, away: 67.7 },
  { model: 'Elo', home: 73.1, away: 65.9 },
  { model: 'Log. Reg.', home: 71.9, away: 64.6 },
];

const GOALIE_IMPACT = [
  { scenario: 'Gardien confirmé', accuracy: 81.4 },
  { scenario: 'Gardien non confirmé', accuracy: 68.7 },
  { scenario: 'Backup inattendu', accuracy: 61.2 },
  { scenario: 'Gardien top 10', accuracy: 79.8 },
  { scenario: 'Gardien recrue', accuracy: 65.3 },
];

const TOOLTIP_STYLE = {
  backgroundColor: '#111827',
  border: '1px solid #1E2D4A',
  borderRadius: '6px',
  color: '#E8EDF5',
  fontSize: '11px',
};

const SUMMARY_STATS = [
  { label: 'Précision globale', value: '74.3%', trend: '+2.1%', color: 'text-primary' },
  { label: 'Précision domicile', value: '78.1%', trend: '+1.8%', color: 'text-positive' },
  { label: 'Précision extérieur', value: '70.5%', trend: '+2.4%', color: 'text-accent' },
  { label: 'ROI analytique', value: '+12.3%', trend: '+3.1%', color: 'text-warning' },
];

export default function BacktestingClient() {
  const [selectedModel, setSelectedModel] = useState('rf');
  const [selectedSeason, setSelectedSeason] = useState('2024-25');

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Backtesting System</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Validation analytique — Projections historiques vs résultats réels
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedSeason}
            onChange={e => setSelectedSeason(e.target.value)}
            className="bg-muted border border-border rounded px-3 py-1.5 text-sm text-foreground"
          >
            {['2024-25', '2023-24', '2022-23'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="btn-primary flex items-center gap-2">
            <Icon name="PlayIcon" size={14} />
            Lancer backtest
          </button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {SUMMARY_STATS.map((stat) => (
          <div key={stat.label} className="card stat-card-hover p-4">
            <p className="text-2xs uppercase tracking-wider text-muted-foreground mb-2">{stat.label}</p>
            <p className={`text-2xl font-bold font-mono-data ${stat.color}`}>{stat.value}</p>
            <p className="text-xs text-positive mt-1 font-mono-data">{stat.trend} vs saison précédente</p>
          </div>
        ))}
      </div>

      {/* Accuracy curves */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="ArrowTrendingUpIcon" size={14} className="text-primary" />
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Courbes de précision — Tous les modèles</span>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={ACCURACY_TIMELINE}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: '#6B7A99', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis domain={[63, 77]} tick={{ fill: '#6B7A99', fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [`${v}%`]} />
            <Legend wrapperStyle={{ fontSize: '10px', color: '#6B7A99' }} />
            <Line type="monotone" dataKey="rf" name="Random Forest" stroke="#00D4FF" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="xgb" name="XGBoost" stroke="#FF6B35" strokeWidth={1.5} dot={false} strokeDasharray="5 3" />
            <Line type="monotone" dataKey="lgbm" name="LightGBM" stroke="#00C853" strokeWidth={1.5} dot={false} strokeDasharray="5 3" />
            <Line type="monotone" dataKey="elo" name="Elo" stroke="#FFB800" strokeWidth={1.5} dot={false} strokeDasharray="5 3" />
            <Line type="monotone" dataKey="lr" name="Log. Reg." stroke="#A855F7" strokeWidth={1.5} dot={false} strokeDasharray="5 3" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Team accuracy + Home/Away */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Team accuracy heatmap */}
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="TableCellsIcon" size={14} className="text-accent" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Précision par équipe — Random Forest</span>
          </div>
          <div className="space-y-2">
            {TEAM_ACCURACY.map((t) => (
              <div key={t.team} className="flex items-center gap-3">
                <span className="text-xs font-mono-data text-foreground w-8">{t.team}</span>
                <div className="flex-1 bg-muted rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${t.accuracy}%`,
                      backgroundColor: t.accuracy >= 78 ? '#00C853' : t.accuracy >= 74 ? '#00D4FF' : t.accuracy >= 70 ? '#FFB800' : '#FF3B5C'
                    }}
                  />
                </div>
                <span className={`text-xs font-mono-data w-12 text-right ${t.accuracy >= 78 ? 'text-positive' : t.accuracy >= 74 ? 'text-primary' : t.accuracy >= 70 ? 'text-warning' : 'text-negative'}`}>
                  {t.accuracy}%
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Home/Away comparison */}
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="HomeIcon" size={14} className="text-primary" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Précision domicile vs extérieur</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={HOME_AWAY_DATA} layout="vertical" barSize={10}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" horizontal={false} />
              <XAxis type="number" domain={[60, 82]} tick={{ fill: '#6B7A99', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="model" tick={{ fill: '#6B7A99', fontSize: 10 }} axisLine={false} tickLine={false} width={80} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [`${v}%`]} />
              <Legend wrapperStyle={{ fontSize: '10px', color: '#6B7A99' }} />
              <Bar dataKey="home" name="Domicile" fill="#00D4FF" radius={[0, 2, 2, 0]} />
              <Bar dataKey="away" name="Extérieur" fill="#FF6B35" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Goalie impact + ROI */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="ShieldCheckIcon" size={14} className="text-positive" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Impact gardien sur précision</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={GOALIE_IMPACT} barSize={20}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" vertical={false} />
              <XAxis dataKey="scenario" tick={{ fill: '#6B7A99', fontSize: 9 }} axisLine={false} tickLine={false} />
              <YAxis domain={[55, 85]} tick={{ fill: '#6B7A99', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [`${v}%`]} />
              <Bar dataKey="accuracy" name="Précision" radius={[3, 3, 0, 0]}>
                {GOALIE_IMPACT.map((entry, index) => (
                  <Cell key={index} fill={entry.accuracy >= 78 ? '#00C853' : entry.accuracy >= 70 ? '#00D4FF' : '#FF3B5C'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="CurrencyDollarIcon" size={14} className="text-warning" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">ROI analytique par saison</span>
          </div>
          <div className="space-y-4 mt-2">
            {[
              { season: '2024-25', roi: 12.34, games: 847, model: 'Random Forest' },
              { season: '2023-24', roi: 10.21, games: 820, model: 'Random Forest' },
              { season: '2022-23', roi: 8.76, games: 795, model: 'XGBoost' },
              { season: '2021-22', roi: 7.43, games: 780, model: 'Elo' },
            ].map((row) => (
              <div key={row.season} className="flex items-center gap-3">
                <span className="text-xs font-mono-data text-muted-foreground w-16">{row.season}</span>
                <div className="flex-1 bg-muted rounded-full h-2">
                  <div className="bg-warning h-2 rounded-full" style={{ width: `${(row.roi / 15) * 100}%` }} />
                </div>
                <span className="text-xs font-mono-data text-warning w-14 text-right">+{row.roi}%</span>
                <span className="text-2xs text-muted-foreground w-20 text-right">{row.games} matchs</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
